import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';
import type {
  CreateOutreachSendPayload,
  CreateOutreachTemplatePayload,
  DueContact,
  FollowUpContactOverride,
  FollowUpStageCadence,
  OutreachBrandCard,
  OutreachContactStage,
  OutreachDraft,
  OutreachEngagementModel,
  OutreachSend,
  OutreachTemplate,
  OutreachTemplateQuery,
  UpdateOutreachTemplatePayload,
} from '../types';

/**
 * osprey-api `/outreach/*` (PE-2188 FE, backend PE-2187) — templates, brand cards, AI drafts,
 * the send log and the follow-up cadence.
 *
 * TCS-staff only: every controller carries SALES_OUTREACH.READ at class level and MANAGE on
 * each write. Not merchant- or tenant-scoped (a contact is a prospect, not a merchant), so
 * `/outreach` is deliberately absent from ApiFetch's scoped prefixes — like `/sales`.
 *
 * Every list returns a bare array; `unwrap` is kept so a later `{ data }` envelope can't
 * silently empty the UI (the taxonomy bare-array lesson, inverted).
 */
export const outreachApi = {
  listTemplates: async (query: OutreachTemplateQuery = {}): Promise<OutreachTemplate[]> => {
    const search = new URLSearchParams();
    if (query.engagementModel) search.append('engagementModel', query.engagementModel);
    if (query.channel) search.append('channel', query.channel);
    if (query.isActive !== undefined) search.append('isActive', String(query.isActive));
    const qs = search.toString();
    const response = await ApiFetch.get<Enveloped<OutreachTemplate[]>>(
      `/outreach/templates${qs ? `?${qs}` : ''}`,
    );
    return unwrap(response);
  },

  createTemplate: async (payload: CreateOutreachTemplatePayload): Promise<OutreachTemplate> => {
    const response = await ApiFetch.post<Enveloped<OutreachTemplate>>('/outreach/templates', payload);
    return unwrap(response);
  },

  /** PATCH — `ApiFetch.put` issues PATCH, which is what osprey-api declares here. */
  updateTemplate: async (
    id: string,
    payload: UpdateOutreachTemplatePayload,
  ): Promise<OutreachTemplate> => {
    const response = await ApiFetch.put<Enveloped<OutreachTemplate>>(
      `/outreach/templates/${id}`,
      payload,
    );
    return unwrap(response);
  },

  /** `null` until someone generates one — the GET 404s with "No brand card generated…". */
  getBrandCard: async (contactId: string): Promise<OutreachBrandCard | null> => {
    const response = await ApiFetch.getOrNull<Enveloped<OutreachBrandCard>>(
      `/outreach/contacts/${contactId}/brand-card`,
    );
    return response ? unwrap(response) : null;
  },

  /** Deterministic, assembled from the contact's own fields — regenerating overwrites it. */
  generateBrandCard: async (contactId: string): Promise<OutreachBrandCard> => {
    const response = await ApiFetch.post<Enveloped<OutreachBrandCard>>(
      `/outreach/contacts/${contactId}/brand-card`,
      {},
    );
    return unwrap(response);
  },

  /**
   * A stateless AI suggestion — nothing is stored or sent. The caller reviews and edits it, and
   * only `logSend` records anything. 503 when the LLM call fails.
   */
  generateDraft: async (
    contactId: string,
    engagementModel: OutreachEngagementModel,
  ): Promise<OutreachDraft> => {
    const response = await ApiFetch.post<Enveloped<OutreachDraft>>(
      `/outreach/contacts/${contactId}/drafts`,
      { engagementModel },
    );
    return unwrap(response);
  },

  /** Newest first. */
  listSends: async (contactId: string): Promise<OutreachSend[]> => {
    const response = await ApiFetch.get<Enveloped<OutreachSend[]>>(
      `/outreach/contacts/${contactId}/sends`,
    );
    return unwrap(response);
  },

  /** Records a message the staff member sent themselves — osprey-api does not deliver it. */
  logSend: async (contactId: string, payload: CreateOutreachSendPayload): Promise<OutreachSend> => {
    const response = await ApiFetch.post<Enveloped<OutreachSend>>(
      `/outreach/contacts/${contactId}/sends`,
      payload,
    );
    return unwrap(response);
  },

  /** Excludes SIGNED / ACTIVE_MERCHANT / LOST; a contact never sent to is always due. */
  listDue: async (): Promise<DueContact[]> => {
    const response = await ApiFetch.get<Enveloped<DueContact[]>>('/outreach/followup/due');
    return unwrap(response);
  },

  /** Only stages someone has configured — the rest fall back to the 7-day default. */
  listStageCadences: async (): Promise<FollowUpStageCadence[]> => {
    const response = await ApiFetch.get<Enveloped<FollowUpStageCadence[]>>(
      '/outreach/followup/cadence/stages',
    );
    return unwrap(response);
  },

  /** A real PUT (upsert), not PATCH — hence `legacy_put`. */
  setStageCadence: async (
    stage: OutreachContactStage,
    intervalDays: number,
  ): Promise<FollowUpStageCadence> => {
    const response = await ApiFetch.legacy_put<Enveloped<FollowUpStageCadence>>(
      '/outreach/followup/cadence/stages',
      { stage, intervalDays },
    );
    return unwrap(response);
  },

  /** Resolves with an empty body (→ null) when the contact has no override. */
  getContactCadence: async (contactId: string): Promise<FollowUpContactOverride | null> => {
    const response = await ApiFetch.get<Enveloped<FollowUpContactOverride> | undefined>(
      `/outreach/followup/cadence/contacts/${contactId}`,
    );
    return response ? unwrap(response) : null;
  },

  /** `intervalDays: null` clears the override, falling back to the stage cadence. */
  setContactCadence: async (
    contactId: string,
    intervalDays: number | null,
  ): Promise<FollowUpContactOverride | null> => {
    const response = await ApiFetch.legacy_put<Enveloped<FollowUpContactOverride> | undefined>(
      `/outreach/followup/cadence/contacts/${contactId}`,
      { intervalDays },
    );
    return response ? unwrap(response) : null;
  },
};
